import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/Finances.css';

const Finances = ({ onBack }) => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Optimistically use user data from location state if available
  const initialUser = location.state?.user;

  useEffect(() => {
    if (userId) {
      fetchUserDetails(userId);
    }
  }, [userId]);

  const fetchUserDetails = async (id) => {
    try {
      setLoading(true);
      const res = await api.get(`/admin-management/users/${id}/details`);
      setDetails(res.data?.data || null);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.clear();
        window.location.href = '/login';
        return;
      }
      console.error('Failed to fetch user details:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !initialUser) return <div className="finances-container">Loading financial records...</div>;

  const userData = details?.user || initialUser;
  if (!userData) return <div className="finances-container">User not found or ID missing.</div>;

  const bankTransactions = details?.bankTransactions || [];
  const payments = details?.payments || [];
  const balances = details?.balances || { totalRoundupBalance: "0.00" };
  const loanAccounts = details?.loanAccounts || [];

  const handleExportBankTransactions = () => {
    const roundUpTxns = bankTransactions.filter(t => t.isRoundedUp);
    const targetTxns = roundUpTxns.length > 0 ? roundUpTxns : bankTransactions;

    if (targetTxns.length === 0) {
      alert('No bank transactions available to export.');
      return;
    }

    const headers = [
      'Transaction ID',
      'Merchant Name',
      'Original Amount ($)',
      'Roundup Amount ($)',
      'Transaction Date',
      'Is Rounded Up'
    ];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return `"${str}"`;
    };

    const rows = targetTxns.map(t => {
      const absAmount = Math.abs(t.amount || 0);
      const roundupAmount = Math.ceil(absAmount) - absAmount;
      return [
        t.id ?? '',
        t.merchantName || 'Transaction',
        absAmount.toFixed(2),
        roundupAmount.toFixed(2),
        t.transactionDate ? new Date(t.transactionDate).toLocaleDateString('en-US') : (t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-US') : 'N/A'),
        t.isRoundedUp !== undefined ? t.isRoundedUp : true
      ];
    });

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const uName = (userData.firstName ? `${userData.firstName}_${userData.lastName}` : userData.name || `user_${userId || 'details'}`).replace(/\s+/g, '_');
    link.setAttribute('download', `${uName}_bank_transactions_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportRecentPayments = () => {
    if (payments.length === 0) {
      alert('No recent payments available to export.');
      return;
    }

    const headers = [
      'ID',
      'Charge ID',
      'User ID',
      'Description',
      'Status',
      'Amount',
      'Payment Type',
      'Failure Reason',
      'Refunded At'
    ];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return `"${str}"`;
    };

    const rows = payments.map(pay => [
      pay.id ?? '',
      pay.chargeId ?? '',
      pay.userId ?? '',
      pay.description ?? '',
      pay.status ?? '',
      pay.amount ?? '',
      pay.paymentType ?? '',
      pay.failureReason ?? '',
      pay.refundedAt ?? ''
    ]);

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const uName = (userData.firstName ? `${userData.firstName}_${userData.lastName}` : userData.name || `user_${userId || 'details'}`).replace(/\s+/g, '_');
    link.setAttribute('download', `${uName}_roundup_payments_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="finances-container">
      <div className="finance-header-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button className="back-pill" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            <span>Back</span>
          </button>
          <div className="user-profile-header">
            <div className="profile-avatar">{(userData.firstName || userData.name || 'U').charAt(0)}</div>
            <div style={{ minWidth: 0 }}>
              <h1 className="user-name">{userData.firstName ? `${userData.firstName} ${userData.lastName}` : userData.name}</h1>
              <p className="user-email">{userData.email}</p>
            </div>
          </div>
        </div>

        <div className="finance-stats">
          <div
            className="stat-pill primary"
            onClick={() => navigate('/ledger', { state: { selectedUser: userData, from: location.pathname } })}
            style={{ cursor: 'pointer' }}
          >
            <span className="label">Total Roundup Balance</span>
            <span className="value">${Number(balances.totalRoundupBalance || 0).toFixed(2)}</span>
          </div>
          <div className="stat-pill" style={{ background: '#f8f9fa', border: '1px solid #eee' }}>
            <span className="label" style={{ color: '#6F767E' }}>Current Round-up amount</span>
            <span className="value" style={{ color: '#1A1D1F' }}>${Number(balances.fractionalBalance || 0).toFixed(2)}</span>
          </div>
          <div
            className={`stat-pill secondary ${userData.subscriptionStatus?.toLowerCase() === 'active' ? 'status-active' : 'status-inactive'}`}
            onClick={() => navigate('/subscription', { state: { selectedUser: userData, from: location.pathname } })}
            style={{ cursor: 'pointer' }}
          >
            <span className="label">Subscription</span>
            <span className="value" style={{ textTransform: 'capitalize' }}>{userData.subscriptionStatus || 'N/A'}</span>
          </div>
        </div>
      </div>

      <div className="profile-details-grid">
        <div className="detail-card">
          <span className="label">Role</span>
          <span className="value">{userData.role || 'User'}</span>
        </div>
        <div className="detail-card">
          <span className="label">Phone</span>
          <span className="value">{userData.phoneNumber || 'N/A'}</span>
        </div>
        <div className="detail-card">
          <span className="label">Plaid Status</span>
          <span className={`status-tag ${userData.plaidStatus ? 'completed' : 'delayed'}`}>
            {userData.plaidStatus ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        <div className="detail-card">
          <span className="label">Date of Installation</span>
          <span className="value">{new Date(userData.createdAt).toLocaleDateString('en-US')}</span>
        </div>
        <div className="detail-card">
          <span className="label">Plaid Auth Date</span>
          <span className="value">
            {userData.cardAccounts && userData.cardAccounts.length > 0
              ? new Date(Math.min(...userData.cardAccounts.map(a => new Date(a.createdAt)))).toLocaleDateString('en-US')
              : 'N/A'}
          </span>
        </div>
      </div>

      <div className="finance-content-grid">
        <section className="transactions-section">
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <h2>Bank Transactions</h2>
            <button
              className="btn-export-csv"
              onClick={handleExportBankTransactions}
              disabled={bankTransactions.length === 0}
              title="Export Bank Transactions CSV"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>Export CSV</span>
            </button>
          </div>
          <div className="transaction-list">
            {bankTransactions.filter(t => t.isRoundedUp).length === 0 ? (
              <div style={{ padding: '20px', color: '#6F767E' }}>No round-up transactions found</div>
            ) : bankTransactions.filter(t => t.isRoundedUp).map(item => {
              const absAmount = Math.abs(item.amount);
              const roundupAmount = Math.ceil(absAmount) - absAmount;

              return (
                <div key={item.id} className="transaction-card">
                  <div className="transaction-left">
                    <div className="merchant-logo">
                      {(item.merchantName || 'T').charAt(0)}
                    </div>
                    <div className="merchant-details">
                      <h3>{item.merchantName || 'Transaction'}</h3>
                      <p style={{ fontSize: '13px', color: '#6F767E' }}>Original: ${absAmount.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="transaction-right">
                    <div style={{ textAlign: 'right' }}>
                      <span className="amount" style={{ color: '#10b981', fontWeight: '700' }}>
                        +${roundupAmount.toFixed(2)}
                      </span>
                      <p style={{ fontSize: '11px', color: '#6F767E', margin: '4px 0 0 0' }}>Roundup</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="finance-side-column">
          <section
            className="payouts-section interactive-section"
            style={{ marginBottom: '24px' }}
          >
            <div className="section-header">
              <h2>Linked Loan Accounts</h2>
            </div>
            <div className="payout-list">
              {loanAccounts.length === 0 ? (
                <div style={{ padding: '20px', color: '#6F767E' }}>No loan accounts linked</div>
              ) : loanAccounts.map((loan) => (
                <div
                  key={loan.id}
                  className="loan-detail-card-mini"
                  onClick={(e) => {
                    console.log("userData", userData);

                    e.stopPropagation();
                    navigate('/loans', {
                      state: {
                        selectedUser: loan,
                        from: location.pathname,
                        name: `${userData?.firstName || ''} ${userData?.lastName || ''}`,
                        email: userData?.email || '',
                      }
                    });
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="loan-card-header">
                    <span className="lender-name">{loan.lenderName || 'N/A'}</span>
                    <span className="loan-type-tag">{loan.loanType}</span>
                  </div>
                  <div className="loan-card-body">
                    <div className="loan-info-row">
                      <span>Original Balance:</span>
                      <strong>${loan.originalBalance?.toLocaleString()}</strong>
                    </div>
                    <div className="loan-info-row">
                      <span>Interest Rate:</span>
                      <strong>{loan.interestRate}%</strong>
                    </div>
                    <div className="loan-info-row">
                      <span>Term:</span>
                      <strong>{loan.loanTerm} months</strong>
                    </div>
                    <div className="loan-info-row">
                      <span>Account Number:</span>
                      <strong>{loan.accountNumber}</strong>
                    </div>
                    <div className="loan-info-row">
                      <span>Origination Date:</span>
                      <strong>{loan.originationDate ? new Date(loan.originationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="payouts-section">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', justifyContent: 'end' }}>
              <div style={{ justifyContent: 'center' }} ><h2>Recent Round-Up Payments</h2></div>
              <button
                className="btn-export-csv"
                onClick={handleExportRecentPayments}
                disabled={payments.length === 0}
                title="Export Recent Round-Up Payments CSV"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span>Export CSV</span>
              </button>
            </div>
            <div className="payout-list">
              {payments.length === 0 ? (
                <div style={{ padding: '20px', color: '#6F767E' }}>No payments found</div>
              ) : payments.map(pay => (
                <div key={pay.id} className="payout-item">
                  <div className="payout-date">{new Date(pay.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}</div>
                  <div className="payout-amount positive">+${Number(pay.amount).toFixed(2)}</div>
                  <div className="payout-status">{pay.status}</div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Finances;

